import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '漫画皮',
    url: 'http://qjmd.cn/',
    description: aboutInfo.mission,
    foundingDate: aboutInfo.founded,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T1-B座 1806室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.businessEmail,
        contactType: 'business',
        availableLanguage: ['Chinese'],
      },
      {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.copyrightEmail,
        contactType: 'copyright',
        availableLanguage: ['Chinese'],
      },
    ],
    sameAs: [
      'http://qjmd.cn/',
    ],
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdOrganization)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Brand Intro */}
            <section className="py-10 md:py-14">
              <div className="flex flex-col lg:flex-row gap-10 items-start">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4 whitespace-nowrap">
                    关于漫画皮
                  </span>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-6 leading-tight">
                    {aboutInfo.slogan}
                  </h1>
                  <p className="text-foreground-500 text-lg leading-relaxed mb-4">
                    {aboutInfo.mission}
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    {aboutInfo.story}
                  </p>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[400px] flex flex-col gap-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20creative%20office%20space%20with%20comic%20illustrations%20on%20walls%2C%20modern%20workspace%20with%20wooden%20desks%20and%20warm%20lighting%2C%20creative%20team%20atmosphere%2C%20clean%20aesthetic%2C%20cozy%20and%20artistic%2C%20warm%20amber%20and%20cream%20tones&width=600&height=400&seq=about-team-mangapi&orientation=landscape"
                    alt="漫画皮团队 - 创作环境"
                    className="w-full rounded-lg object-cover"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20digital%20dashboard%20display%20with%20comic%20reading%20statistics%2C%20warm%20color%20scheme%2C%20clean%20data%20visualization%2C%20creative%20tech%20atmosphere%2C%20professional%20yet%20artistic%2C%20amber%20and%20cream%20tones&width=600&height=400&seq=about-data-mangapi&orientation=landscape"
                    alt="漫画皮 - 平台数据看板"
                    className="w-full rounded-lg object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="py-10 border-t border-background-200/70">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-extrabold text-accent-500 mb-1">
                    {aboutInfo.stats.totalComics.toLocaleString()}+
                  </div>
                  <div className="text-sm text-foreground-500">漫画作品</div>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-extrabold text-accent-500 mb-1">
                    {(aboutInfo.stats.totalChapters / 10000).toFixed(0)}万+
                  </div>
                  <div className="text-sm text-foreground-500">漫画章节</div>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-extrabold text-accent-500 mb-1">
                    {(aboutInfo.stats.dailyActiveUsers / 10000).toFixed(0)}万+
                  </div>
                  <div className="text-sm text-foreground-500">日活跃用户</div>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-extrabold text-accent-500 mb-1">
                    {(aboutInfo.stats.totalUsers / 10000).toFixed(0)}万+
                  </div>
                  <div className="text-sm text-foreground-500">累计用户</div>
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="py-10 border-t border-background-200/70">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">
                我们的<span className="text-accent-500">价值观</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.values.map((v) => (
                  <div key={v.title} className="bg-background-100 rounded-lg p-6">
                    <h3 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Media */}
            <section className="py-10 border-t border-background-200/70">
              <div className="mb-8">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-3 whitespace-nowrap">
                  媒体评价
                </span>
                <h2 className="text-2xl font-bold text-foreground-950">
                  行业<span className="text-accent-500">声誉</span>
                </h2>
              </div>
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
                {aboutInfo.media.map((m) => (
                  <div key={m.outlet} className="break-inside-avoid mb-5 bg-background-100 rounded-lg p-6">
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      <i className="ri-double-quotes-l text-accent-300 mr-1"></i>
                      {m.quote}
                      <i className="ri-double-quotes-r text-accent-300 ml-1"></i>
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{m.outlet}</span>
                      <span className="text-xs text-foreground-400">{m.year}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="py-10 border-t border-background-200/70">
              <h2 className="text-2xl font-bold text-foreground-950 mb-3 text-center">
                商务<span className="text-accent-500">合作</span>
              </h2>
              <p className="text-foreground-500 text-center mb-8">
                版权引进 · 广告合作 · 品牌联名
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-500"></i>
                      <span>商务合作：{aboutInfo.contact.businessEmail}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-copyright-line text-accent-500"></i>
                      <span>版权事务：{aboutInfo.contact.copyrightEmail}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-service-line text-accent-500"></i>
                      <span>合作洽谈：{aboutInfo.contact.cooperationEmail}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line text-accent-500"></i>
                      <span>电话：{aboutInfo.contact.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">公司地址</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <div className="flex items-start gap-2">
                      <i className="ri-map-pin-line text-accent-500 mt-0.5"></i>
                      <span>{aboutInfo.contact.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-wechat-line text-accent-500"></i>
                      <span>微信：{aboutInfo.contact.wechat}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-links-line text-accent-500"></i>
                      <span>微博：{aboutInfo.contact.siteLink}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="py-10 border-t border-background-200/70">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center">
                常见<span className="text-accent-500">问题</span>
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {aboutInfo.faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg">
                    <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-950 hover:text-accent-500 transition-colors">
                      {faq.question}
                      <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
